import { todayIso } from '@shkola/api';
import { Link } from '@tanstack/react-router';
import { CalendarDays, ListChecks, Video } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { CalendarDotsIcon } from '../design/icons';
import { Clouds } from '../design/kit';
import { type Look, useLook } from '../design/looks';
import { OnColorProvider, StatusPill, buttonClass, useAge } from '../design/ui';
import { useMinute } from '../lib/now';
import { AbroadCard } from './AbroadPage';
import { SchoolArt } from './home/Art';
import { type TodoCard, useTodoCards } from './home/Cards';
import { DayBlock, useDayHeight } from './home/Day';
import { FreshHome } from './home/FreshHome';
import { Glass2Home } from './home/Glass2Home';
import { Shapes2Home } from './home/Shapes2Home';
import { ShapesHome } from './home/ShapesHome';
import { GlassHome } from './home/GlassHome';
import { useHeadline } from './home/headline';
import { type StudentContext, dayLong } from './home/model';

/*
 * The student's home (UX of 26.10, docs/ux-home-student.md, on the look of 27.10): open it and see at
 * once what goes on now and what to do.
 *  — the greeting: «привіт,» and the name, where the day stands in one chip, one sentence about what
 *    matters now, a line of details and the main button; beside it the school with its clock;
 *  — the day: today, after the lessons the next school day (Day.tsx);
 *  — what to do as cards by urgency: late, soon, the tests of the week, to redo, to confirm, to catch
 *    up (Cards.tsx) — the two most urgent beside the day, the rest in a row under it.
 * Two looks to choose from on the preview (sky, warm): the same content, their own colours. «Скло»
 * (GlassHome.tsx), «Скло 2», «Свіжість», «Форми» and «Форми 2» are the composition agreed on 27.10, each
 * a home of its own.
 */

/** The two bright looks of this home; the others are homes of their own. */
type Bright = Exclude<Look, 'glass' | 'glass2' | 'shapes' | 'shapes2' | 'fresh'>;

const HERO: Record<Bright, string> = {
  sky: [
    'radial-gradient(45% 90% at 88% 0%, rgb(214 204 252 / 0.7) 0%, rgb(214 204 252 / 0) 70%)',
    'radial-gradient(40% 80% at 0% 100%, rgb(200 238 226 / 0.6) 0%, rgb(200 238 226 / 0) 70%)',
    'linear-gradient(180deg, #B4D1F0 0%, #CBE0F5 55%, #E1ECF8 100%)',
  ].join(', '),
  warm: [
    'radial-gradient(50% 120% at 100% 0%, rgb(226 216 252 / 0.9) 0%, rgb(226 216 252 / 0) 70%)',
    'radial-gradient(45% 110% at 0% 100%, rgb(255 222 200 / 0.9) 0%, rgb(255 222 200 / 0) 70%)',
    'linear-gradient(120deg, #FFE9DB 0%, #FCE4EA 52%, #EDE6FB 100%)',
  ].join(', '),
};

/** «привіт,» by hand and the name: the one handwritten line of the screen. */
function Greeting({ name }: { name: string }) {
  const { t } = useTranslation();
  const age = useAge();
  return (
    <h1 className="flex flex-wrap items-baseline gap-x-3">
      <span className="-rotate-3 pr-1 font-hand text-[34px] leading-10 font-bold text-ink-soft md:text-[36px]">
        {t('studentHome.hi')}
      </span>
      <span
        className={`font-display font-semibold tracking-[-0.03em] text-ink ${age === 'junior' ? 'text-[44px] leading-[52px] md:text-[52px] md:leading-[56px]' : 'text-[40px] leading-[48px] md:text-[48px] md:leading-[52px]'}`}
      >
        {name}
      </span>
    </h1>
  );
}

/** On a phone the two buttons of the greeting fit one line. */
const PHONE_BUTTON = 'max-sm:h-11 max-sm:px-4 max-sm:text-[14px]';

function Greeter({ ctx, look }: { ctx: StudentContext; look: Bright }) {
  const { t } = useTranslation();
  const now = useMinute();
  const head = useHeadline(ctx);
  return (
    <section
      className="rim relative min-w-0 overflow-hidden rounded-[28px] p-4 md:p-6 xl:col-span-12"
      style={{ backgroundImage: HERO[look] }}
    >
      {look === 'sky' && (
        <Clouds
          blur={18}
          duration={46}
          puffs={[
            ['-4%', '-30%', 420, 170, 0.8],
            ['38%', '30%', 380, 150, 0.5],
          ]}
        />
      )}
      <SchoolArt
        now={now}
        className="absolute right-2 bottom-0 hidden w-[300px] lg:block xl:right-6 xl:w-[400px]"
      />
      <div className="relative flex min-w-0 flex-col gap-4 lg:max-w-[calc(100%-300px)] xl:max-w-[calc(100%-420px)]">
        <div className="flex flex-col gap-1.5">
          <p className="flex items-center gap-2 text-[14px] leading-5 font-medium text-ink-soft">
            <CalendarDotsIcon
              size={16}
              weight="fill"
              aria-hidden
              className="text-tone-blue-vivid"
            />
            {dayLong(todayIso())}
            {ctx.child.class?.label && (
              <span className="text-muted">· {ctx.child.class.label}</span>
            )}
          </p>
          <Greeting name={ctx.child.firstName} />
        </div>
        <div className="flex min-h-[104px] flex-col items-start gap-2">
          {head.ready && (
            <>
              <OnColorProvider value>
                <StatusPill
                  tone={head.chip.tone}
                  icon={head.chip.icon}
                  dot={head.chip.live}
                  size="md"
                  className="max-w-full"
                >
                  <span className="truncate">{head.chip.text}</span>
                </StatusPill>
              </OnColorProvider>
              <p className="font-display text-[20px] leading-7 font-semibold tracking-[-0.01em] text-ink md:text-[23px] md:leading-8">
                {head.main}
              </p>
              {head.details.length > 0 && (
                <p className="text-[15px] leading-6 text-ink-soft">{head.details.join(' · ')}</p>
              )}
            </>
          )}
        </div>
        <div className="flex flex-wrap gap-2">
          {head.join ? (
            <Link
              to="/lesson/$lessonId"
              params={{ lessonId: head.join.id }}
              className={`${buttonClass('primary', 'lg')} ${PHONE_BUTTON}`}
            >
              <Video size={18} strokeWidth={1.8} aria-hidden />
              {t('student.lessons.joinLesson')}
            </Link>
          ) : (
            <Link to="/homework" className={`${buttonClass('primary', 'lg')} ${PHONE_BUTTON}`}>
              <ListChecks size={18} strokeWidth={1.8} aria-hidden />
              {t('nav.homework')}
            </Link>
          )}
          <Link
            to="/schedule"
            className={`glass inline-flex h-12 shrink-0 items-center justify-center gap-2 rounded-full px-6 text-[15px] font-semibold whitespace-nowrap text-ink transition-colors hover:bg-white ${PHONE_BUTTON}`}
          >
            <CalendarDays size={18} strokeWidth={1.8} aria-hidden />
            {t('schedule.title')}
          </Link>
        </div>
      </div>
    </section>
  );
}

const ROW_COLUMNS = ['', 'xl:grid-cols-1', 'xl:grid-cols-2', 'xl:grid-cols-3', 'xl:grid-cols-4'];

export function StudentHome({ ctx }: { ctx: StudentContext }) {
  const look = useLook();
  if (look === 'glass') return <GlassHome ctx={ctx} />;
  if (look === 'glass2') return <Glass2Home ctx={ctx} />;
  if (look === 'shapes') return <ShapesHome ctx={ctx} />;
  if (look === 'shapes2') return <Shapes2Home ctx={ctx} />;
  if (look === 'fresh') return <FreshHome ctx={ctx} />;
  return <BrightHome ctx={ctx} look={look} />;
}

/** The two bright looks: the greeting, the day, the cards of what to do. */
function BrightHome({ ctx, look }: { ctx: StudentContext; look: Bright }) {
  const { cards } = useTodoCards(ctx);
  const day = useDayHeight(ctx);
  // beside the day — the most urgent cards that fit its height (at least one, at most three); a list
  // with more rows than it shows takes the height left; the rest go in a row under the day
  let split = 1;
  let height = cards[0]?.height ?? 0;
  while (split < Math.min(3, cards.length)) {
    const next = (cards[split] as TodoCard).height;
    if (height + 24 + next > day + 16) break;
    height += 24 + next;
    split += 1;
  }
  // a single card left over joins them rather than stand alone in its row
  if (cards.length - split === 1 && split < 3) split += 1;
  const beside = cards.slice(0, split);
  const under = cards.slice(split);
  const grower = beside.find((c) => c.grows) ?? beside.at(-1);
  return (
    <div className="grid grid-cols-1 gap-4 md:gap-6 xl:grid-cols-12">
      <Greeter ctx={ctx} look={look} />
      <div className="min-w-0 empty:hidden xl:col-span-12">
        <AbroadCard studentId={ctx.child.memberId} self />
      </div>
      <DayBlock ctx={ctx} className="xl:col-span-7" />
      <div className="flex min-w-0 flex-col gap-4 md:gap-6 xl:col-span-5">
        {beside.map((c) => (
          <div
            key={c.key}
            className={`flex min-w-0 flex-col [&>*]:flex-1 ${c === grower ? 'flex-1' : ''}`}
          >
            {c.render(c === grower && c.grows, false)}
          </div>
        ))}
      </div>
      {under.length > 0 && (
        <div
          className={`grid grid-cols-1 gap-4 sm:grid-cols-2 md:gap-6 xl:col-span-12 ${ROW_COLUMNS[Math.min(4, under.length)]}`}
        >
          {under.map((c) => (
            <div key={c.key} className="flex min-w-0 flex-col [&>*]:flex-1">
              {c.render(false, under.length === 1)}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
